/**
 * Theme color injection: generate --zoom-color-* CSS variables
 * from Base Color (H,S,L) following the rules in brand-color.md.
 */

const BRAND_STYLE_ID = 'brand-color-sheet'

/** Luminance thresholds for color adjustment strategies */
const LUMINANCE = {
  /** L >= 80% is treated as an ultra-light theme color */
  LIGHT_THRESHOLD: 80,
  /** L <= 20% is treated as an ultra-dark theme color */
  DARK_THRESHOLD: 20
}

/** Opacity values for subtle interactive states */
const OPACITY = {
  SUBTLE_HOVER: 0.12,
  SUBTLE_PRESS: 0.42
}

/** Lightness offsets for generating color variants (from brand-color.md) */
const L_OFFSET = {
  // Format: { normal, light, dark } corresponds to luminance ranges
  TEXT_PRIMARY: { normal: 0, light: -40, dark: 40 },
  BORDER_PRIMARY: { normal: 15, light: -30, dark: 30 },
  STATE_HOVER: { normal: -10, light: -15, dark: 15 },
  STATE_PRESS: { normal: -25, light: -30, dark: 30 },
  FILL_SUBTLER: { normal: 48, light: 0, dark: 70 },
  STATE_SUBTLE: { normal: 0, light: -20, dark: 20 },
  BORDER_SUBTLE: { normal: 35, light: -15, dark: 50 },
  TOGGLE_BG: { normal: 45, light: -5, dark: 60 },
  TEXT_STRONG: { normal: -15, light: -50, dark: 50 },
  TEXT_STRONGER: { normal: -35, light: -60, dark: 60 },
  STATE_DISABLE: { normal: 36, light: -10, dark: 15 }
}

/**
 * Convert a hex color to HSL (0-360, 0-100, 0-100). Values are not rounded.
 * @param {string} hex - e.g. #0D6BDE or #0D6
 * @returns {{ h: number, s: number, l: number } | null}
 */
export function hexToHSL(hex) {
  if (!hex || typeof hex !== 'string') return null
  const raw = hex.replace(/^#/, '')
  const digits =
    raw.length === 3
      ? raw
          .split('')
          .map((c) => c + c)
          .join('')
      : raw
  if (!/^[0-9a-f]{6}$/i.test(digits)) return null

  const r = parseInt(digits.slice(0, 2), 16) / 255
  const g = parseInt(digits.slice(2, 4), 16) / 255
  const b = parseInt(digits.slice(4, 6), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const delta = max - min
  const l = (max + min) / 2

  let h = 0
  let s = 0
  if (delta !== 0) {
    s = delta / (1 - Math.abs(2 * l - 1))
    if (max === r) h = ((g - b) / delta) % 6
    else if (max === g) h = (b - r) / delta + 2
    else h = (r - g) / delta + 4
    h = h * 60
    if (h < 0) h += 360
  }

  return {
    h,
    s: s * 100,
    l: l * 100
  }
}

/**
 * Inject theme color styles into the page: write --zoom-color-* variables on body.
 * @param {string} [brandColor] - Brand color hex string, e.g. #0D6BDE; skip when empty.
 */
export function applyBrandColor(brandColor) {
  if (!brandColor) return
  const hsl = hexToHSL(brandColor)
  if (!hsl) return

  const cssVars = buildThemeCSSVars(hsl)
  const extraRules = buildBrandExtraRules()
  const styleEl = getOrCreateStyleElement()
  styleEl.textContent = `body {\n${cssVars}\n}\n\n${extraRules}`

  document.body.classList.add('is-brand-color-applied')
}

function getOrCreateStyleElement() {
  let el = document.getElementById(BRAND_STYLE_ID)
  if (!el) {
    el = document.createElement('style')
    el.id = BRAND_STYLE_ID
    el.setAttribute('type', 'text/css')
    document.body.insertBefore(el, document.body.firstChild)
  }
  return el
}

/**
 * Build theme CSS variables based on the mapping table in brand-color.md.
 * Formula column order: normal (L>20 & L<80) | ultra-light (L>=80) | ultra-dark (L<=20)
 */
function buildThemeCSSVars({ h, s, l }) {
  const isLight = l >= LUMINANCE.LIGHT_THRESHOLD
  const isDark = l <= LUMINANCE.DARK_THRESHOLD

  const clampL = (delta) => Math.min(100, Math.max(0, l + delta))

  /** Pick L offset by luminance range: normal / light / dark. */
  const pickL = (offset) => {
    if (isLight) return offset.light
    if (isDark) return offset.dark
    return offset.normal
  }

  // Unrounded HSL is kept for math; CSS text only needs two decimals
  const css = (n) => Math.round(n * 100) / 100

  const hslValue = (offset, alpha) => {
    const lVal = clampL(pickL(offset))
    const core = `${css(h)}, ${css(s)}%, ${css(lVal)}%`
    return alpha != null ? `hsla(${core}, ${alpha})` : `hsl(${core})`
  }

  const brand = `hsl(${css(h)}, ${css(s)}%, ${css(l)}%)`

  // One-to-one mapping with the variable table in brand-color.md
  const vars = {
    // fill-primary / fill-global-primary: Brand | Brand | Brand
    '--zoom-color-fill-global-primary': brand,
    '--zoom-color-fill-primary': brand,
    // text-primary: Brand | L-40 | L+40
    '--zoom-color-text-primary': hslValue(L_OFFSET.TEXT_PRIMARY),
    // border-primary: L+15 | L-30 | L+30
    '--zoom-color-border-primary': hslValue(L_OFFSET.BORDER_PRIMARY),
    // state-primary-hover: L-10 | L-15 | L+15
    '--zoom-color-state-primary-hover': hslValue(L_OFFSET.STATE_HOVER),
    // state-primary-press: L-25 | L-30 | L+30
    '--zoom-color-state-primary-press': hslValue(L_OFFSET.STATE_PRESS),
    // fill-subtler-primary: L+48 | L(0) | L+70
    '--zoom-color-fill-subtler-primary': hslValue(L_OFFSET.FILL_SUBTLER),
    // state-subtle-primary-hover: Brand 12% | L-20 12% | L+20 12%
    '--zoom-color-state-subtle-primary-hover': hslValue(
      L_OFFSET.STATE_SUBTLE,
      OPACITY.SUBTLE_HOVER
    ),
    // state-subtle-primary-press: Brand 42% | L-20 42% | L+20 42%
    '--zoom-color-state-subtle-primary-press': hslValue(
      L_OFFSET.STATE_SUBTLE,
      OPACITY.SUBTLE_PRESS
    ),
    // border-subtle-primary: L+35 | L-15 | L+50
    '--zoom-color-border-subtle-primary': hslValue(L_OFFSET.BORDER_SUBTLE),
    // toggle-button-bg-selected: L+45 | L-5 | L+60
    '--zoom-color-toggle-button-bg-selected': hslValue(L_OFFSET.TOGGLE_BG),
    // text-strong-primary: L-15 | L-50 | L+50
    '--zoom-color-text-strong-primary': hslValue(L_OFFSET.TEXT_STRONG),
    // text-stronger-primary: L-35 | L-60 | L+60
    '--zoom-color-text-stronger-primary': hslValue(L_OFFSET.TEXT_STRONGER),
    // state-subtle-primary-disable: L+36 | L-10 | L+15 (checkbox disabled color)
    '--zoom-color-state-subtle-primary-disable': hslValue(L_OFFSET.STATE_DISABLE)
  }

  return Object.entries(vars)
    .map(([name, value]) => `  ${name}: ${value};`)
    .join('\n')
}

/**
 * Component-specific CSS rules that apply brand color variables.
 * These rules map UI components to the appropriate CSS variables.
 */
const ACTIVE_COMPONENT_RULES = [
  // Buttons
  {
    selector: '.zm-button--primary',
    styles: { 'background-color': 'var(--zoom-color-fill-global-primary, #0d6bde)' }
  },
  { selector: '.zm-button--text', styles: { color: 'var(--zoom-color-text-primary, #0d6bde)' } },
  {
    selector: '.zm-button--text:hover',
    styles: { color: 'var(--zoom-color-state-primary-hover, #0d6bde)' }
  },

  // Breadcrumb
  {
    selector: '.zm-breadcrumb__inner a, .zm-breadcrumb__inner.is-link',
    styles: { color: 'var(--zoom-color-text-primary, #0d6bde)' }
  },

  // Tabs
  {
    selector: '.zm-tabs__item.is-active',
    styles: { color: 'var(--zoom-color-text-primary, #0d6bde)' }
  },
  {
    selector: '.zm-tabs__item.is-active:after',
    styles: { 'background-color': 'var(--zoom-color-fill-global-primary, #0d6bde)' }
  },
  {
    selector: '.zm-tabs__item.is-active h2',
    styles: { color: 'var(--zoom-color-text-primary, #0d6bde) !important' }
  },

  // Tab Navigation
  {
    selector: '.zm-tabnav>li>a:hover',
    styles: { color: 'var(--zoom-color-state-primary-hover, #0d6bde)' }
  },
  {
    selector: '.zm-tabnav>li.active>a, .zm-tabnav>li.active>a:hover, .zm-tabnav>li.active>a:focus',
    styles: {
      color: 'var(--zoom-color-text-primary, #0d6bde)',
      'border-bottom-color': 'var(--zoom-color-fill-global-primary, #0d6bde)'
    }
  },

  // Checkbox
  {
    selector: '.zm-checkbox__label.is-checked .zm-checkbox__inner',
    styles: {
      'background-color': 'var(--zoom-color-fill-global-primary, #0d6bde)',
      'border-color': 'var(--zoom-color-fill-global-primary, #0d6bde)'
    }
  },
  {
    selector: '.zm-checkbox__label.is-checked:hover .zm-checkbox__inner',
    styles: {
      'background-color': 'var(--zoom-color-state-primary-hover, #0d6bde)',
      'border-color': 'var(--zoom-color-state-primary-hover, #0d6bde)'
    }
  },

  // Switch
  {
    selector: '.zm-switch.is-checked .zm-switch__core',
    styles: { 'background-color': 'var(--zoom-color-fill-global-primary, #0d6bde)' }
  },
  {
    selector: '.zm-switch.is-checked .zm-switch__core:hover',
    styles: { 'background-color': 'var(--zoom-color-state-primary-hover, #0d6bde)' }
  }
]

/**
 * Generate CSS rules from component style configuration.
 */
function buildBrandExtraRules() {
  return ACTIVE_COMPONENT_RULES.map(({ selector, styles }) => {
    const styleStr = Object.entries(styles)
      .map(([prop, value]) => `  ${prop}: ${value};`)
      .join('\n')
    return `${selector} {\n${styleStr}\n}`
  }).join('\n\n')
}
